import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

export interface RemotePage<T> {
  totalCount: number;
  data: T[];
}

/**
 * Pages of a list the backend pages, for a table with app-paginator: the
 * data source with remote paging of the publications page. A newer request
 * cancels the one in flight.
 */
export class RemotePages<T> {
  public items: T[] = [];
  public totalRecords = 0;
  public pageIndex = 0;
  public error: string | null = null;
  public readonly loading$ = new BehaviorSubject<boolean>(false);

  private readonly requests = new Subject<number>();
  private readonly subscription: Subscription;

  constructor(
    fetch: (page: number, pageSize: number) => Observable<RemotePage<T>>,
    public readonly pageSize = 10
  ) {
    this.subscription = this.requests
      .pipe(
        tap(() => this.loading$.next(true)),
        switchMap((page) =>
          fetch(page, this.pageSize).pipe(
            map((result) => ({ page, result, error: null as string | null })),
            catchError((e: Error) =>
              of({ page, result: null, error: e.message })
            ),
            finalize(() => this.loading$.next(false))
          )
        )
      )
      .subscribe(({ page, result, error }) => {
        this.error = error;
        if (result) {
          this.pageIndex = page;
          this.items = result.data;
          this.totalRecords = result.totalCount;
        }
      });
  }

  /** Index of the first row of the page, the `first` of app-paginator. */
  public get first() {
    return this.pageIndex * this.pageSize;
  }

  public load(pageIndex: number) {
    this.requests.next(pageIndex);
  }

  public clearError() {
    this.error = null;
  }

  public destroy() {
    this.subscription.unsubscribe();
    this.loading$.complete();
  }
}
